"use client";
import * as React from "react";
import { useState, useEffect } from "react";

type Keys = keyof typeof INITIAL_VALUES;
type Values = (typeof INITIAL_VALUES)[Keys];
import SecretInputFormControl from "../../../../shared/inputs/SecretInputFormControl/index";
import ToggleConnectionStatus from "../../ToggleConnectionStatus/index";
import type {
  EditOIDCConnectionProps,
  FormObj,
  OIDCSSOConnection,
  OIDCSSORecord,
  OIDCFormState,
} from "../../types";
import { saveConnection, deleteConnection } from "../../utils";
import defaultClasses from "./index.module.css";
import cssClassAssembler from "../../../utils/cssClassAssembler";
import Button from "../../../../shared/Button/index";
import Spacer from "../../../../shared/Spacer/index";
import ConfirmationPrompt from "../../../../shared/ConfirmationPrompt/index";
import InputField from "../../../../shared/inputs/InputField/index";
import Separator from "../../../../shared/Separator/index";
import Card from "../../../../shared/Card/index";
import { InputWithCopyButton } from "../../../../shared";
import LoadingContainer from "../../../../shared/LoadingContainer/index";
import { ApiResponse, sendHTTPRequest } from "../../../../shared/http";
import ItemList from "../../../../shared/inputs/ItemList/index";
const INITIAL_VALUES = {
  name: "",
  tenant: "",
  product: "",
  description: "",
  redirectUrl: [""],
  defaultRedirectUrl: "",
  oidcClientSecret: "",
  oidcClientId: "",
  oidcDiscoveryUrl: "",
  "oidcMetadata.issuer": "",
  "oidcMetadata.authorization_endpoint": "",
  "oidcMetadata.token_endpoint": "",
  "oidcMetadata.jwks_uri": "",
  "oidcMetadata.userinfo_endpoint": "",
} as OIDCFormState;

function EditOIDCConnection(props: EditOIDCConnectionProps) {
  const [oidcConnection, setOidcConnection] = useState(() => INITIAL_VALUES);

  const [isConnectionLoading, setIsConnectionLoading] = useState(() => true);

  const [isSaving, setIsSaving] = useState(() => false);

  const [showDelConfirmation, setShowDelConfirmation] = useState(() => false);

  function toggleDelConfirmation() {
    setShowDelConfirmation(!showDelConfirmation);
  }

  function formVariant() {
    return props.variant || "basic";
  }

  function classes() {
    return {
      formDiv: cssClassAssembler(
        props.classNames?.formDiv,
        defaultClasses.formDiv
      ),
      label: cssClassAssembler(props.classNames?.label, defaultClasses.label),
      inputField: {
        label: props.classNames?.label,
        input: props.classNames?.input,
        container: props.classNames?.fieldContainer,
      },
      textarea: {
        label: props.classNames?.label,
        textarea: props.classNames?.textarea,
        container: props.classNames?.fieldContainer,
      },
      section: cssClassAssembler(
        props.classNames?.section,
        defaultClasses.section
      ),
    };
  }

  function isExcluded(fieldName: keyof OIDCSSOConnection) {
    return !!(props.excludeFields as (keyof OIDCSSOConnection)[])?.includes(
      fieldName
    );
  }

  function updateConnection(data: Partial<OIDCSSOConnection>) {
    return {
      ...oidcConnection,
      ...data,
    };
  }

  function handleChange(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const id = target.id as Keys;
    const targetValue = (
      event.currentTarget as HTMLInputElement | HTMLTextAreaElement
    )?.value;
    setOidcConnection(
      updateConnection({
        [id]: targetValue,
      })
    );
  }

  function handleItemListUpdate(fieldName: string, listValue: string[]) {
    setOidcConnection(
      updateConnection({
        [fieldName]: listValue,
      })
    );
  }

  function resetOIDCMetadataFields() {
    const currentVal = oidcConnection;
    setOidcConnection({
      ...currentVal,
      "oidcMetadata.issuer": "",
      "oidcMetadata.authorization_endpoint": "",
      "oidcMetadata.token_endpoint": "",
      "oidcMetadata.jwks_uri": "",
      "oidcMetadata.userinfo_endpoint": "",
    });
  }

  function saveSSOConnection(event: Event) {
    event.preventDefault();
    const formObj: any = {
      connectionIsOIDC: true,
    };
    Object.entries(oidcConnection).map(([key, val]) => {
      if (key.startsWith("oidcMetadata.")) {
        if (formObj.oidcMetadata === undefined) {
          formObj.oidcMetadata = {} as OIDCSSOConnection["oidcMetadata"];
        }
        formObj.oidcMetadata![key.replace("oidcMetadata.", "")] = val;
      } else if (key === "sortOrder") {
        // pass sortOrder only if set to non-empty string
        val !== "" && (formObj[key] = +val); // convert sortOrder into number
      } else {
        formObj[key as keyof Omit<OIDCSSOConnection, "oidcMetadata">] = val;
      }
    });
    setIsSaving(true);
    saveConnection<undefined>({
      url: props.urls.patch,
      isEditView: true,
      formObj: formObj as FormObj,
      connectionIsOIDC: true,
      callback: async (data) => {
        setIsSaving(false);
        if (data?.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(data.error.message);
        } else {
          if (oidcConnection.oidcDiscoveryUrl) {
            resetOIDCMetadataFields();
          }
          typeof props.successCallback === "function" &&
            props.successCallback({
              operation: "UPDATE",
              connection: formObj,
              connectionIsOIDC: true,
            });
        }
      },
    });
  }

  function deleteSSOConnection(event: Event) {
    event.preventDefault();
    deleteConnection({
      url: props.urls.delete,
      clientId: oidcConnection.clientID!,
      clientSecret: oidcConnection.clientSecret!,
      callback: async (data: ApiResponse<undefined>) => {
        if (data?.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(data.error.message);
        } else {
          typeof props.successCallback === "function" &&
            props.successCallback({
              operation: "DELETE",
              connectionIsOIDC: true,
            });
        }
      },
    });
  }

  function shouldDisplayHeader() {
    if (props.displayHeader !== undefined) {
      return props.displayHeader;
    }
    return true;
  }

  function shouldDisplayInfoCard() {
    if (props.displayInfo !== undefined) {
      return props.displayInfo;
    }
    return true;
  }

  function connectionFetchUrl() {
    return props.urls.get;
  }

  useEffect(() => {
    async function getConnection(url: string) {
      const data = await sendHTTPRequest<OIDCSSORecord[]>(url);
      setIsConnectionLoading(false);
      if (data && typeof data === "object") {
        if ("error" in data && data.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(data.error.message);
        } else if (Array.isArray(data)) {
          const _connection = data[0];
          if (_connection) {
            setOidcConnection({
              ..._connection,
              name: _connection.name || "",
              label: _connection.label || "",
              tenant: _connection.tenant || "",
              product: _connection.product || "",
              description: _connection.description || "",
              redirectUrl: _connection.redirectUrl,
              defaultRedirectUrl: _connection.defaultRedirectUrl,
              oidcClientId: _connection.oidcProvider.clientId || "",
              oidcClientSecret: _connection.oidcProvider.clientSecret || "",
              oidcDiscoveryUrl: _connection.oidcProvider.discoveryUrl || "",
              "oidcMetadata.issuer":
                _connection.oidcProvider.metadata?.issuer || "",
              "oidcMetadata.authorization_endpoint":
                _connection.oidcProvider.metadata?.authorization_endpoint || "",
              "oidcMetadata.token_endpoint":
                _connection.oidcProvider.metadata?.token_endpoint || "",
              "oidcMetadata.jwks_uri":
                _connection.oidcProvider.metadata?.jwks_uri || "",
              "oidcMetadata.userinfo_endpoint":
                _connection.oidcProvider.metadata?.userinfo_endpoint || "",
              sortOrder: _connection.sortOrder ?? "",
            });
          }
        }
      }
    }
    getConnection(connectionFetchUrl());
  }, [connectionFetchUrl()]);

  return (
    <LoadingContainer isBusy={isConnectionLoading}>
      <div className={classes().formDiv}>
        <div className={defaultClasses.headingContainer}>
          {shouldDisplayHeader() ? (
            <h5 className={defaultClasses.h5}>Edit SSO Connection</h5>
          ) : null}
          <ToggleConnectionStatus
            connection={oidcConnection}
            urls={{
              patch: props.urls.patch,
            }}
            classNames={{
              confirmationPrompt: {
                button: {
                  ctoa: `${
                    props.classNames?.confirmationPrompt?.button?.ctoa
                  } ${
                    oidcConnection.deactivated
                      ? props.classNames?.button?.ctoa
                      : props.classNames?.button?.destructive
                  }`.trim(),
                  cancel: props.classNames?.confirmationPrompt?.button?.cancel,
                },
              },
            }}
            errorCallback={props.errorCallback}
            successCallback={props.successCallback}
          />
        </div>
        <div>
          <form method="post" onSubmit={(event) => saveSSOConnection(event)}>
            {formVariant() === "advanced" ? (
              <>
                {!isExcluded("name") ? (
                  <>
                    <InputField
                      label="Connection name (Optional)"
                      id="name"
                      placeholder="MyApp"
                      classNames={classes().inputField}
                      required={false}
                      value={oidcConnection.name || ""}
                      handleInputChange={handleChange}
                    />
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("label") ? (
                  <>
                    <InputField
                      label="Connection label (Optional)"
                      id="label"
                      placeholder="An internal label to identify the connection"
                      classNames={classes().inputField}
                      required={false}
                      value={oidcConnection.label!}
                      handleInputChange={handleChange}
                    />
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("description") ? (
                  <>
                    <InputField
                      label="Description (Optional)"
                      id="description"
                      placeholder="A short description not more than 100 characters"
                      classNames={classes().inputField}
                      required={false}
                      maxLength={100}
                      value={oidcConnection.description || ""}
                      handleInputChange={handleChange}
                    />
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("redirectUrl") ? (
                  <>
                    <ItemList
                      inputType="url"
                      label="Allowed redirect URLs"
                      fieldName="redirectUrl"
                      currentlist={oidcConnection.redirectUrl}
                      handleItemListUpdate={handleItemListUpdate}
                      classNames={classes().inputField}
                    />
                    <div id="redirectUrl-hint" className={defaultClasses.hint}>
                      URL(s) to redirect the user to after login. Only the URLs
                      in this list are allowed in the OAuth flow.
                    </div>
                    <Spacer y={6} />
                  </>
                ) : null}
                {!isExcluded("defaultRedirectUrl") ? (
                  <>
                    <InputField
                      label="Default redirect URL"
                      id="defaultRedirectUrl"
                      aria-describedby="defaultRedirectUrl-hint"
                      placeholder="http://localhost:3366/login/saml"
                      type="url"
                      classNames={classes().inputField}
                      required
                      value={oidcConnection.defaultRedirectUrl || ""}
                      handleInputChange={handleChange}
                    />
                    <div
                      id="defaultRedirectUrl-hint"
                      className={defaultClasses.hint}
                    >
                      URL to redirect the user to after an IdP initiated login.
                    </div>
                    <Spacer y={6} />
                  </>
                ) : null}
              </>
            ) : null}
            <InputField
              label="Client ID [OIDC Provider]"
              id="oidcClientId"
              aria-describedby="oidc-clientid-hint"
              required
              readOnly
              classNames={classes().inputField}
              value={oidcConnection.oidcClientId || ""}
            />
            <Spacer y={6} />
            <SecretInputFormControl
              label="Client Secret [OIDC Provider]"
              id="oidcClientSecret"
              classNames={{
                input: props.classNames?.secretInput,
              }}
              value={oidcConnection.oidcClientSecret}
              required
              readOnly={false}
              copyDoneCallback={props.successCallback}
              handleChange={handleChange}
            />
            <Spacer y={6} />
            <InputField
              id="oidcDiscoveryUrl"
              type="url"
              label="Well-known URL of OpenID Provider"
              placeholder="https://example.com/.well-known/openid-configuration"
              aria-describedby="oidc-metadata-hint"
              classNames={classes().inputField}
              value={oidcConnection.oidcDiscoveryUrl || ""}
              handleInputChange={handleChange}
            />
            <div id="oidc-metadata-hint" className={defaultClasses.hint}>
              Enter the well known discovery path of OpenID provider or manually
              enter the OpenId provider metadata below.
            </div>
            <Spacer y={6} />
            <Separator text="OR" />
            <Spacer y={6} />
            <InputField
              id="oidcMetadata.issuer"
              label="Issuer"
              placeholder="https://example.com"
              classNames={classes().inputField}
              value={oidcConnection["oidcMetadata.issuer"]!}
              handleInputChange={handleChange}
            />
            <Spacer y={6} />
            <InputField
              id="oidcMetadata.authorization_endpoint"
              type="url"
              label="Authorization Endpoint"
              placeholder="https://example.com/oauth/authorize"
              classNames={classes().inputField}
              value={oidcConnection["oidcMetadata.authorization_endpoint"]!}
              handleInputChange={handleChange}
            />
            <Spacer y={6} />
            <InputField
              id="oidcMetadata.token_endpoint"
              type="url"
              label="Token endpoint"
              placeholder="https://example.com/oauth/token"
              classNames={classes().inputField}
              value={oidcConnection["oidcMetadata.token_endpoint"]!}
              handleInputChange={handleChange}
            />
            <Spacer y={6} />
            <InputField
              id="oidcMetadata.jwks_uri"
              type="url"
              label="JWKS URI"
              placeholder="https://example.com/.well-known/jwks.json"
              classNames={classes().inputField}
              value={oidcConnection["oidcMetadata.jwks_uri"]!}
              handleInputChange={handleChange}
            />
            <Spacer y={6} />
            <InputField
              id="oidcMetadata.userinfo_endpoint"
              type="url"
              label="UserInfo endpoint"
              placeholder="https://example.com/userinfo"
              classNames={classes().inputField}
              value={oidcConnection["oidcMetadata.userinfo_endpoint"]!}
              handleInputChange={handleChange}
            />
            <Spacer y={6} />
            {formVariant() === "advanced" ? (
              <>
                {!isExcluded("sortOrder") ? (
                  <>
                    <InputField
                      label="Sort Order"
                      id="sortOrder"
                      type="number"
                      min="0"
                      placeholder="10"
                      classNames={classes().inputField}
                      value={oidcConnection.sortOrder as string}
                      handleInputChange={handleChange}
                    />
                    <div id="sortOrder-hint" className={defaultClasses.hint}>
                      Connections will be sorted (in a listing view like IdP
                      Selection) using this setting. Higher values will be
                      displayed first.
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            <div className={defaultClasses.formAction}>
              {typeof props.cancelCallback === "function" ? (
                <Button
                  type="button"
                  name="Cancel"
                  variant="outline"
                  handleClick={props.cancelCallback}
                  classNames={props.classNames?.button?.cancel}
                />
              ) : null}
              <Button
                type="submit"
                name="Save"
                classNames={props.classNames?.button?.ctoa}
                isLoading={isSaving}
              />
            </div>
            <Spacer y={6} />
            {shouldDisplayInfoCard() ? (
              <Card
                title="Connection info"
                variant="info"
                arrangement="vertical"
              >
                <div className={defaultClasses.info}>
                  {formVariant() === "advanced" ? (
                    <>
                      {!isExcluded("tenant") ? (
                        <>
                          <InputField
                            label="Tenant"
                            id="tenant"
                            placeholder="acme.com"
                            classNames={classes().inputField}
                            required
                            readOnly
                            value={oidcConnection.tenant!}
                          />
                          <Spacer y={6} />
                        </>
                      ) : null}
                      {!isExcluded("product") ? (
                        <>
                          <InputField
                            label="Product"
                            id="product"
                            placeholder="demo"
                            classNames={classes().inputField}
                            required
                            readOnly
                            value={oidcConnection.product!}
                          />
                          <Spacer y={6} />
                        </>
                      ) : null}
                    </>
                  ) : null}
                  <InputWithCopyButton
                    label="Client ID"
                    text={oidcConnection.clientID || ""}
                    classNames={classes().inputField}
                    copyDoneCallback={props.successCallback}
                  />
                  <Spacer y={6} />
                  <SecretInputFormControl
                    label="Client Secret"
                    id="clientSecret"
                    classNames={{
                      input: props.classNames?.secretInput,
                    }}
                    value={oidcConnection.clientSecret}
                    required
                    readOnly
                    copyDoneCallback={props.successCallback}
                    handleChange={handleChange}
                  />
                </div>
                <Spacer y={6} />
              </Card>
            ) : null}
            <Spacer y={4} />
            {oidcConnection.clientID && oidcConnection.clientSecret ? (
              <section className={classes().section}>
                <div className={defaultClasses.info}>
                  <h6 className={defaultClasses.sectionHeading}>
                    Delete this connection
                  </h6>
                  <p className={defaultClasses.sectionPara}>
                    All your apps using this connection will stop working.
                  </p>
                </div>
                {!showDelConfirmation ? (
                  <Button
                    name="Delete"
                    variant="destructive"
                    type="button"
                    handleClick={toggleDelConfirmation}
                    classNames={props.classNames?.button?.destructive}
                  />
                ) : null}
                {showDelConfirmation ? (
                  <ConfirmationPrompt
                    ctoaVariant="destructive"
                    promptMessage="Are you sure you want to delete the Connection? This action cannot be undone and will permanently delete the Connection."
                    classNames={{
                      button: {
                        ctoa: `${props.classNames?.button?.destructive} ${props.classNames?.confirmationPrompt?.button?.ctoa}`.trim(),
                        cancel:
                          props.classNames?.confirmationPrompt?.button?.cancel,
                      },
                    }}
                    cancelCallback={toggleDelConfirmation}
                    confirmationCallback={deleteSSOConnection}
                  />
                ) : null}
              </section>
            ) : null}
          </form>
        </div>
      </div>
    </LoadingContainer>
  );
}

export default EditOIDCConnection;
